import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Ruler, Gem, Mountain, Factory, TrainFront, HardHat, Map as MapIcon } from 'lucide-react';
import Reveal from '../components/Reveal';
import PageHeader from '../components/PageHeader';
import { images, projectFacts, projectFactsFootnote, projectSpecs, elCapitanSpecs, mining, miningTitle, miningIntro, usgsMap } from '../mock';
import projectHero from '../assets/project-magnesite.jpg';
import usgsMapImage from '../assets/usgs-critical-minerals-map.jpg';

const miningIcons = [Mountain, Factory, TrainFront, HardHat];

const Project = () => {
  return (
    <div>
      <PageHeader
        eyebrow="The Project"
        title="Whitemoon & El Capitan"
        subtitle="Bedded sedimentary magnesite deposits in San Bernardino County, California."
        image={projectHero}
      />

      {/* OVERVIEW */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="eyebrow text-amber-700 mb-5">Overview</div>
              <h2 className="font-display text-3xl md:text-4xl font-medium leading-tight text-neutral-900">
                Two deposits, one high-purity resource.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-8">
            {projectFacts.map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="flex gap-6 border-t border-neutral-200 pt-6">
                  <span className="font-display text-amber-700 text-lg font-semibold shrink-0">0{i + 1}</span>
                  <p className="text-neutral-700 leading-relaxed text-lg">{f}</p>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <p className="border-t border-neutral-200 pt-6 text-neutral-400 text-sm leading-relaxed italic">
                {projectFactsFootnote}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="bg-neutral-950 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <Ruler className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
              <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Whitemoon</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {projectSpecs.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 80} className="bg-neutral-950">
                <div className="px-8 py-6">
                  <div className="eyebrow text-neutral-400 text-[0.6rem] mb-3">{s.k}</div>
                  <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.v}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE SPLIT */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal>
              <Layers className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                A shallow basin lens, unusually pure.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-neutral-600 leading-relaxed text-lg">
                Mineralization occurs as a thin lens roughly 600 ft wide and 2,400 ft long. Classified by the California Geological Society as unusually pure, with ~1M mt exposed at surface.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <Link to="/history" className="mt-10 group inline-flex items-center gap-2 eyebrow text-neutral-900 border-b-2 border-amber-700 pb-1 hover:gap-4 transition-all duration-300">
                Explore the history <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal>
            <div className="overflow-hidden">
              <img
                src={images.projectWhitemoon}
                alt="Aerial view of the Whitemoon magnesite deposit"
                className="w-full h-[480px] object-cover hover:scale-105 transition-transform duration-1000"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* EL CAPITAN STATS */}
      <section className="bg-neutral-950 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <Ruler className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
              <h2 className="font-display text-2xl md:text-3xl font-medium text-white">El Capitan</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {elCapitanSpecs.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 80} className="bg-neutral-950">
                <div className="px-8 py-6">
                  <div className="eyebrow text-neutral-400 text-[0.6rem] mb-3">{s.k}</div>
                  <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.v}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EL CAPITAN IMAGE SPLIT */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal>
              <Gem className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                Two steeply-dipping beds.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-neutral-600 leading-relaxed text-lg">
                A companion bedded sedimentary magnesite deposit sitting within the same 100%-owned claim block. First mapped by the USGS in 1943, El Capitan shares the geology of Whitemoon and adds meaningful upside to the wider project area.
              </p>
            </Reveal>
          </div>
          <Reveal>
            <div className="overflow-hidden">
              <img
                src={images.ecShaftAerial}
                alt="Aerial view of the Whitemoon shaft collar and claim area"
                className="w-full h-[480px] object-cover hover:scale-105 transition-transform duration-1000"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MINING & LOGISTICS */}
      <section className="py-28 bg-stone-50 border-t border-neutral-200" data-testid="project-mining-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <Mountain className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
                <div className="eyebrow text-amber-700 mb-4">Mining & Logistics</div>
                <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                  {miningTitle}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={100}>
                <p className="text-neutral-700 leading-relaxed text-lg">{miningIntro}</p>
              </Reveal>
            </div>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-10">
            {mining.map((m, i) => {
              const Icon = miningIcons[i % miningIcons.length];
              return (
                <Reveal key={i} delay={(i % 2) * 90}>
                  <div data-testid={`project-mining-${i}`} className="border-t border-neutral-300 pt-6">
                    <Icon className="w-6 h-6 text-amber-700 mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-xl font-semibold text-neutral-900">{m.title}</h3>
                    <p className="mt-3 text-neutral-600 leading-relaxed">{m.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* USGS CRITICAL MINERALS MAP */}
      <section className="py-28 bg-white border-t border-neutral-200" data-testid="usgs-map-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <MapIcon className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
                <div className="eyebrow text-amber-700 mb-4">{usgsMap.eyebrow}</div>
                <h2 data-testid="usgs-map-title" className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                  {usgsMap.title}
                </h2>
              </Reveal>
              <Reveal delay={120}>
                <p className="mt-8 text-neutral-600 leading-relaxed">{usgsMap.body}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={80}>
                <div className="relative">
                  <img
                    src={usgsMapImage}
                    alt="USGS map of mines and mineral deposits with identified critical mineral resources in the United States, with the Whitemoon deposit annotated in San Bernardino County, California"
                    className="w-full h-auto"
                  />
                  <svg
                    viewBox="0 0 1336 1032"
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    aria-hidden="true"
                  >
                    <circle cx="196" cy="521" r="17" fill="none" stroke="#ffffff" strokeWidth="7" opacity="0.75" />
                    <circle cx="196" cy="521" r="17" fill="none" stroke="#b45309" strokeWidth="3.5" data-testid="usgs-map-marker" />
                    <line x1="185" y1="534" x2="128" y2="592" stroke="#ffffff" strokeWidth="6" opacity="0.75" />
                    <line x1="185" y1="534" x2="128" y2="592" stroke="#b45309" strokeWidth="2.5" />
                    <text
                      x="124"
                      y="622"
                      fill="#b45309"
                      fontSize="23"
                      fontWeight="700"
                      letterSpacing="4"
                      style={{ fontFamily: "'Space Grotesk', sans-serif", paintOrder: 'stroke', stroke: '#ffffff', strokeWidth: 6, strokeLinejoin: 'round' }}
                    >
                      WHITEMOON
                    </text>
                  </svg>
                </div>
                <p className="mt-5 text-neutral-500 text-sm leading-relaxed border-t border-neutral-200 pt-4">
                  {usgsMap.caption}
                  <span className="block mt-1 text-neutral-400 text-xs">{usgsMap.source}</span>
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="relative py-32 overflow-hidden">
        <img src={images.desertMountain} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/80" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <Gem className="w-8 h-8 text-amber-400 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-display text-2xl md:text-4xl font-medium text-white leading-relaxed">
              A mining-friendly county with 90+ active mines - neighboring Castle Mountain and Mountain Pass.
            </h2>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Project;
